<template>
  <div class="waterfall-chart">
    <div :class="justifyClass">
      <PulseAndRiskDefinitions
        :initial="'STABILITY'"
        title="View Pulse descriptions"
      ></PulseAndRiskDefinitions>
    </div>
    <v-skeleton-loader
      v-if="!chartData.baseline"
      type="image"
    ></v-skeleton-loader>

    <v-chart
      v-if="chartData.baseline"
      ref="chartInstance"
      :option="chartOptions"
      style="height: 200px; width: 100%; background-color: transparent;"
    />
  </div>
</template>

<script>
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
} from "echarts/components";

import { BarChart } from "echarts/charts";
use([
  CanvasRenderer,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  BarChart,
  TitleComponent,
]);

import {
  CHART_COLOR_NEGATIVE,
  CHART_COLOR_POSITIVE,
} from "@/common/constants/charts";
import { upperCaseFirstLetter } from "@/common/basic.utilities";
import { getColorByStabilityScore } from "@/services/grids/grids.service";

const Y_AXIS_PADDING = 1;

export default {
  name: "WaterfallChart",
  components: {
    PulseAndRiskDefinitions: () =>
      import("@/components/grids/PulseAndRiskDefinitions"),
  },
  props: {
    chartData: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      required: false,
      default: "",
    },
    descriptionJustify: {
      type: String,
      required: false,
      default: "right",
    },
    refresh: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      justifyClass: `icons-container-${this.descriptionJustify}`,
    };
  },
  computed: {
    chartOptions() {
      return {
        title: {
          text: this.title,
          left: "center",
          top: "5px",
          textStyle: {
            fontSize: 24,
            fontFamily: "Archivo-Light, sans-serif !important",
            color: "rgba(255,255,255,1)",
          },
        },
        stackStrategy: "all",
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          formatter: function (params) {
            const tar = params[1];
            return tar.name + ": " + tar.value;
          },
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          top: "30px",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          splitLine: { show: false },
          axisLabel: {
            interval: 0,
          },
          data: [
            "Baseline\nScore",
            ...Object.keys(this.chartData?.score_adjustments).map((k) =>
              upperCaseFirstLetter(k)
            ),
            "Adjusted\nScore",
          ],
        },
        yAxis: {
          type: "value",
          min: Math.floor(
            Math.min(
              this.chartData?.baseline,
              this.chartData?.score,
              ...this.calculateScoreTotals
            ) - Y_AXIS_PADDING
          ),
          max: Math.ceil(
            Math.max(
              this.chartData?.baseline,
              this.chartData?.score,
              ...this.calculateScoreTotals
            ) + Y_AXIS_PADDING
          ),
        },
        series: [
          {
            name: "TransparentData",
            type: "bar",
            stack: "Total",
            itemStyle: {
              borderColor: "transparent",
              color: "transparent",
            },
            emphasis: {
              itemStyle: {
                borderColor: "transparent",
                color: "transparent",
              },
            },
            data: this.calculateTransparentBarData,
          },
          {
            name: "DisplayData",
            type: "bar",
            stack: "Total",
            label: {
              show: false,
              position: "inside",
            },
            data: this.getColorizedData,
          },
        ],
      };
    },
    calculateTransparentBarData() {
      return [0, ...this.calculateScoreTotals, 0];
    },
    calculateScoreTotals() {
      let r = [];
      Object.values(this.chartData?.score_adjustments).reduce((acc, curr) => {
        r.push(acc);
        return acc + curr;
      }, this.chartData?.baseline);
      return r;
    },
    getColorizedData() {
      return [
        {
          value: this.chartData?.baseline,
          itemStyle: {
            color: getColorByStabilityScore(this.chartData?.baseline),
          },
        },
        ...Object.values(this.chartData?.score_adjustments).map((v) => {
          return {
            value: v,
            itemStyle: {
              color: v > 0 ? CHART_COLOR_POSITIVE : CHART_COLOR_NEGATIVE,
            },
          };
        }),
        {
          value: this.chartData?.score,
          itemStyle: {
            color: getColorByStabilityScore(this.chartData?.score),
          },
        },
      ];
    },
  },
  methods: {
    reloadChart() {
      if (this.$refs.chartInstance) {
        this.$refs.chartInstance.resize();
      }
    },
  },
  watch: {
    refresh: {
      handler() {
        this.reloadChart();
      },
    },
  },
};
</script>

<style scoped>
.waterfall-chart {
  position: relative;
}
.icons-container-right {
  position: absolute;
  top: 3px;
  left: 90%;
  z-index: 400;
}
.icons-container-left {
  position: absolute;
  top: 3px;
  left: 5%;
  z-index: 400;
}
</style>
